import {
  AccommodationHotelsQuery,
  AccommodationPlaceRoomBlockStatus,
  AccommodationPlaceSourceOfData,
  AccommodationPlaceSourceType,
  AddAccommodationPlaceV2Request,
  FindMatchingAccommodationCatalogPropertyQuery,
  UpdateAccommodationPlaceV2Request,
} from '@graphql/generated';
import { AccommodationItemList, TravelMapBlockType } from '../TravelMap.types';
import { CustomPropertyFormFields } from '../pages/CustomProperty/CustomPropertyForm.types';
import { HotelFormFields } from '../pages/Hotel/HotelForm.types';
import { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { PreDefinedGooglePlace } from '@shared/utils/hooks/usePreDefinedGooglePlaces';
import { isNull } from 'lodash-es';
import { formatDateForTravel } from '@shared/utils/dateHelpers';
import { distanceInMiles } from '@shared/utils/distanceInMiles';

interface DetailAddressArgs {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export const getHotelAddress = ({ address, city, state, postalCode, country }: DetailAddressArgs) => {
  const fullAddress = `${address ?? ''}${city ? ', ' + city : ''}${state || postalCode ? ', ' : ''}${state ?? ''}${postalCode ? ' ' + postalCode : ''}${
    country ? ', ' + country : ''
  }`;
  return fullAddress;
};

export const generateAccommodationList = (
  accommodation: AccommodationHotelsQuery['eventByName'],
  origin: { latitude: number; longitude: number } | null | undefined,
): AccommodationItemList[] => {
  return [
    ...(accommodation?.accommodationPlacesV2 || [])
      .filter(accPlaceV2 => isNull(accPlaceV2?.accommodationRoomBlock))
      .map(accPlaceV2 => {
        const distance = distanceInMiles(origin, { lat: accPlaceV2.lat, lng: accPlaceV2.long });
        return {
          id: accPlaceV2.id,
          type: accPlaceV2.isCustomLinkEnabled || !accPlaceV2?.zhId ? TravelMapBlockType.travelMapAccommodationsCustom : TravelMapBlockType.travelMapAccommodationsHotel,
          isFavorite: accPlaceV2.isFavorite || false,
          distance,
          distances: accPlaceV2.distances,
          photoUrl: accPlaceV2.customPhoto?.url || accPlaceV2.photo?.url || '',
          isPhotoHidden: !!accPlaceV2.isPhotoHidden,
          address:
            accPlaceV2.customAddress ||
            getHotelAddress({ address: accPlaceV2.address, city: accPlaceV2.city, state: accPlaceV2.state, postalCode: accPlaceV2.postalCode, country: accPlaceV2.country }) ||
            '',
          cutoffDate: '',
          displayName: accPlaceV2.customName || accPlaceV2.name || '',
          lat: accPlaceV2.lat,
          lng: accPlaceV2.long,
          description: accPlaceV2.description,
          starRating: accPlaceV2?.starRating,
          phone: accPlaceV2.phone,
          customPhone: accPlaceV2.customPhone,
          createdByType: accPlaceV2.createdByType,
          placeId: accPlaceV2.zhId || accPlaceV2.googlePlaceId,
          websiteUrl: accPlaceV2.customUrl || undefined,
          joyPlaceId: accPlaceV2.joyPlaceId,
          currency: 'USD',
          isFromAccommodationCatalog: accPlaceV2.isFromAccommodationCatalog,
          isJoyManagedRoomBlock: false,
        };
      }),
    ...(accommodation?.accommodationPlacesV2 || [])
      .filter(accPlaceV2 => !isNull(accPlaceV2?.accommodationRoomBlock) && accPlaceV2?.accommodationRoomBlock?.status === AccommodationPlaceRoomBlockStatus.active)
      .map(accPlaceV2 => {
        const distance = distanceInMiles(origin, { lat: accPlaceV2.lat, lng: accPlaceV2.long });
        return {
          id: accPlaceV2.id,
          distance,
          distances: accPlaceV2.distances,
          type: TravelMapBlockType.travelMapAccommodationsRoomblock,
          isFavorite: accPlaceV2.isFavorite,
          photoUrl: accPlaceV2.customPhoto?.url || accPlaceV2.photo?.url || '',
          joyPlaceId: accPlaceV2?.joyPlaceId,
          address: accPlaceV2.address || '',
          cutoffDate: accPlaceV2.accommodationRoomBlock?.cutoffDate || '',
          displayName: accPlaceV2.customName || accPlaceV2.name || '',
          isPhotoHidden: false,
          lat: accPlaceV2.lat,
          lng: accPlaceV2.long,
          starRating: accPlaceV2?.starRating,
          createdByType: accPlaceV2.createdByType,
          description: accPlaceV2.description || '',
          price: accPlaceV2.accommodationRoomBlock?.pricePerNight,
          strikeoutPrice: accPlaceV2.accommodationRoomBlock?.strikeoutPricePerNight ?? undefined,
          websiteUrl: accPlaceV2.accommodationRoomBlock?.bookingUrl ?? undefined,
          numberOfRoomsReserved: accPlaceV2.accommodationRoomBlock?.numberOfRoomsReserved ?? 0,
          checkInDate: accPlaceV2.accommodationRoomBlock?.checkInDate ?? '',
          checkOutDate: accPlaceV2.accommodationRoomBlock?.checkOutDate ?? '',
          isJoyManagedRoomBlock: Boolean(accPlaceV2.accommodationRoomBlock?.isJoyManagedRoomBlock),
          // TODO: Get currency from BE
          currency: 'USD',
        };
      }),
  ];
};

/**
 * Reset form fields when change hotel from autocomplete
 */
export const resetHotelFormFields = (updateFormValues: CustomPropertyFormFields | HotelFormFields, isHotelForm?: boolean) => {
  const mutable = updateFormValues as {
    customPhotoId?: string | null;
    photoUrl?: string | null;
    phone?: string | null;
    customPhone?: string | null;
    url?: string | null;
    customUrl?: string | null;
    postalCode?: string | null;
    zhId?: string | null;
    address?: string | null;
  };
  mutable.customPhotoId = null;
  mutable.photoUrl = null;
  mutable.phone = '';
  mutable.customPhone = '';
  mutable.url = null;
  mutable.customUrl = null;
  mutable.postalCode = '';
  mutable.zhId = '';
  if (isHotelForm) {
    mutable.address = '';
  }
};

/**
 * Get place details from google place and align with hotel form
 */
export const updateHotelFormDataFromGooglePlace = (
  updateFormValues: CustomPropertyFormFields | HotelFormFields,
  location: LocationInputV2Response | Location,
  isHotelForm?: boolean,
) => {
  const mutable = updateFormValues as {
    placeId?: string | null;
    googlePlaceId?: string | null;
    country?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    location?: string | null;
  };
  mutable.placeId = location.placeId;
  mutable.googlePlaceId = location.placeId;
  mutable.country = location.country;
  mutable.latitude = location.lat;
  mutable.longitude = location.lng;
  if ('address' in location && location.address && isHotelForm) {
    mutable.location = location.address;
  }
};

/**
 * Get place details from pre-defined place and align with hotel form
 */
export const updateHotelFormDataFromPreDefinedPlace = (
  updateFormValues: CustomPropertyFormFields | HotelFormFields,
  preDefinedPlace: PreDefinedGooglePlace,
  isUpdateCustomUrlLocally?: boolean,
) => {
  const mutable = updateFormValues as {
    customName?: string | null;
    customPhone?: string | null;
    customAddress?: string | null;
    googlePlaceId?: string | null;
    location?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    url?: string | null;
    isCustomUrl?: boolean;
    customUrl?: string | null;
  };
  mutable.customName = preDefinedPlace.name;
  mutable.customPhone = preDefinedPlace.phone;
  mutable.customAddress = preDefinedPlace.formattedAddress || preDefinedPlace.address;
  mutable.googlePlaceId = preDefinedPlace.placeId;
  mutable.location = preDefinedPlace.address;
  mutable.latitude = preDefinedPlace.lat;
  mutable.longitude = preDefinedPlace.lng;
  mutable.url = preDefinedPlace.url;
  if (isUpdateCustomUrlLocally) {
    mutable.isCustomUrl = false;
    mutable.customUrl = preDefinedPlace.url;
  }
};

/**
 * Get place details from accommodations catalog property and align with hotel form
 */
export const updateHotelFormDataFromCatalogProperty = (
  updateFormValues: CustomPropertyFormFields | HotelFormFields,
  findMatchingAccommodationCatalogProperty: FindMatchingAccommodationCatalogPropertyQuery['findMatchingAccommodationCatalogProperty'],
  isUpdateCustomUrlLocally?: boolean,
) => {
  const accommodationCatalogProperty = findMatchingAccommodationCatalogProperty?.accommodationCatalogProperty;
  if (accommodationCatalogProperty) {
    const address = accommodationCatalogProperty.address;
    const mutable = updateFormValues as {
      address?: string | null;
      customAddress?: string | null;
      postalCode?: string | null;
      url?: string | null;
      isCustomUrl?: boolean;
      customUrl?: string | null;
      phone?: string | null;
      customPhone?: string | null;
    };
    mutable.address = getHotelAddress({ ...address, address: address?.street }) ?? '';
    mutable.customAddress = getHotelAddress({ ...address, address: address?.street }) ?? '';
    mutable.postalCode = address?.postalCode ?? '';
    // Don't need this field with new UI, added here to reset from form while update.
    mutable.url = '';
    if (isUpdateCustomUrlLocally) {
      mutable.isCustomUrl = false;
      mutable.customUrl = '';
    }
    mutable.phone = '';
    mutable.customPhone = '';
  }
};

/**
 * Convert accommodation provider id to source of data
 */
type AccommodationProviderIdentifiers = Readonly<{
  isCustomLinkEnabled?: boolean | null;
  googlePlaceId?: string | null;
  joyPlaceId?: string | null;
  zhId?: string | null;
}>;

export const convertAccommodationProviderIdToSourceOfData = (values: AccommodationProviderIdentifiers): AccommodationPlaceSourceOfData | undefined => {
  if (values.isCustomLinkEnabled && values.googlePlaceId) {
    return {
      sourceId: values.googlePlaceId,
      sourceType: AccommodationPlaceSourceType.google,
    };
  } else if (values.joyPlaceId) {
    return {
      sourceId: values.joyPlaceId,
      sourceType: AccommodationPlaceSourceType.joyPlace,
    };
  } else if (values.zhId) {
    return {
      sourceId: values.zhId,
      sourceType: AccommodationPlaceSourceType.zh,
    };
  }
  return undefined;
};

/**
 * Convert all form fields into accommodations api payload
 */
const _convertFormFieldsToAccommodationPayload = (values: CustomPropertyFormFields | HotelFormFields) => {
  const payload: Omit<AddAccommodationPlaceV2Request, 'id' | 'sourceOfData'> | Omit<UpdateAccommodationPlaceV2Request, 'eventId' | 'id' | 'sourceOfData'> = {
    userNote: values.note || '',
    customPhotoId: values.customPhotoId,
    isFavorite: values.isFavorite || false,
    isHostStayingHere: values.isStayingHere || false,
    isPhotoHidden: !values.customPhotoId && !values.photoUrl,
    isCustomLinkEnabled: values.isCustomLinkEnabled || false,
    customUrl: values.customUrl,
    customName: values.customName !== values.name ? values.customName : null,
    customAddress: values.customAddress !== values.address ? values.address : null,
    customPhone: values.customPhone !== values.phone ? values.phone : null,
    category: values.category ?? null,
    ...(values?.isHotelRoomBlock
      ? {
          accommodationRoomBlock: {
            bookingUrl: values.bookingUrl || '',
            checkInDate: formatDateForTravel(values.checkInDate),
            checkOutDate: formatDateForTravel(values.checkOutDate),
            cutoffDate: formatDateForTravel(values.cutoffDate),
            numberOfRoomsReserved: values.numberOfRoomsReserved || 0,
            pricePerNight: values.pricePerNight,
            isJoyManagedRoomBlock: Boolean(values?.isJoyManagedRoomBlock),
            strikeoutPricePerNight: values.strikeoutPricePerNight,
          },
        }
      : {}),
  };
  return payload;
};

/**
 * Convert all form fields into add accommodations api payload
 */
export const convertFormFieldsToAddAccommodationPayload = (eventId: string, values: CustomPropertyFormFields | HotelFormFields) => {
  const payload = _convertFormFieldsToAccommodationPayload(values);
  const sourceOfData = convertAccommodationProviderIdToSourceOfData(values);
  return { ...payload, sourceOfData, eventId };
};

/**
 * Convert all form fields into update accommodations api payload
 */
export const convertFormFieldsToUpdateAccommodationPayload = (id: string, values: CustomPropertyFormFields | HotelFormFields) => {
  const payload = _convertFormFieldsToAccommodationPayload(values);
  const sourceOfData = convertAccommodationProviderIdToSourceOfData(values);
  const addAccommodationPayload: UpdateAccommodationPlaceV2Request = { ...payload, sourceOfData, id };
  return addAccommodationPayload;
};

export const getChatGPTTextCompletionPromptForHotel = (hotelName: string) => {
  return `Your job is to write 2-3 sentence descriptions in the voice of a couple getting married,describing why their guests should stay at this hotel. You can mention proximity or chicness or other unique fun amenities you think wedding guests might enjoy.Should sound casual and conversational.Can you write a short note on why wedding guests should stay at the following hotel? ${hotelName}`;
};

export const getFormattedChatGPTText = (textToFormat: string) => {
  // Remove any leading/trailing whitespace and double quotes
  let text = textToFormat.trim().replace(/"/g, '');

  // If the text starts with a number followed by dot and space (e.g., "1. "), keep it
  if (!/^\d+\.\s/.test(text)) {
    // Otherwise, remove any non-letter characters from the start until we hit a letter
    let i = 0;
    while (i < text.length && !/[A-Za-z]/.test(text[i])) {
      i++;
    }
    text = text.substring(i);
  }

  // Split into lines and trim each line
  // Note: We preserve empty lines to maintain paragraph spacing
  const lines = text.split('\n').map(line => line.trim());

  // Join the lines back together
  return lines.join('\n');
};
